import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, ViewChild, inject, effect, signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { typeEventArgs, ReadyArgs } from 'keycloak-angular';
import { Subject, map, shareReplay, filter, take, combineLatest, takeUntil } from 'rxjs';
import { PrimengTranslationService } from '../../services/primeng-translation-service';
import { selectUser } from '../../../core/state/user/user.selectors';
import { loadUser } from '../../../core/state/user/user.actions';
import { loadCompanies } from '../../../core/state/company/company-context.actions';
import { User } from '../../../core/state/user/user.model';
import { environment } from '../../../../environments/environment';
import { selectCompanies, selectCompanyContext } from '../../../core/state/company/company-context.selectors';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Router } from '@angular/router';
import {
  NavigationItem,
  UserProfile,
  Language,
  EkoLanguageService
} from '@ekowitryna/ui';
import packageJson from '../../../../../package.json';


@Component({
    selector: 'app-hub',
    templateUrl: './hub.component.html',
    styleUrl: './hub.component.css',
    standalone: false
})
export class HubComponent {
  title = 'ekowitryna-esq-calculator';

  keycloakUrl = environment.keycloakUrl;
  keycloakRealm = environment.realm;
  keycloakClientId = environment.clientId;

  hasCompanies = false;
  sidebarCollapsed = false;
  appVersion = packageJson.version;

  @ViewChild('noCompanyDialog') noCompanyDialog: ConfirmDialog | undefined;

  navigationItems: NavigationItem[] = [];
  currentUser: UserProfile | null = null;
  userMenuItems: MenuItem[] = [];
  currentLanguage: string = 'pl';
  availableLanguages: Language[] = [
    { code: 'pl', name: 'Polski' },
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' }
  ];
  pageTitle: string = 'Platforma ESG';
  scrolled = false;
  isInitializing = true;
  destroy$ = new Subject();
  user: User | null = null;
  companies$ = this.store.select(selectCompanies);


  isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset)
  .pipe(
    map(result => result.matches),
    shareReplay()
  );

  private keycloak = inject(Keycloak);

  private languageService = inject(EkoLanguageService);

  constructor(
    private translate: TranslateService,
    private confirmationService: ConfirmationService,
    private breakpointObserver: BreakpointObserver,
    private store: Store,
    public primengTranslationService: PrimengTranslationService,
    public router: Router
  ) {
    // Use KEYCLOAK_EVENT_SIGNAL as per documentation
    const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

    effect(() => {
      const keycloakEvent = keycloakSignal();

      if (keycloakEvent.type === KeycloakEventType.Ready) {
        const authenticated = typeEventArgs<ReadyArgs>(keycloakEvent.args);

        if (authenticated) {
          // If Ready event says authenticated=true, trust it and dispatch
          // The async methods will wait for instance to be ready internally
          this.store.dispatch(loadUser());
          this.store.dispatch(loadCompanies());
        } else {
          this.isInitializing = false;
        }
      }

      // Also listen for AuthSuccess as backup
      if (keycloakEvent.type === KeycloakEventType.AuthSuccess) {
        if (this.keycloak?.authenticated && this.keycloak?.token) {
          this.store.dispatch(loadUser());
          this.store.dispatch(loadCompanies());
        }
      }

      if (keycloakEvent.type === KeycloakEventType.AuthError || keycloakEvent.type === KeycloakEventType.AuthLogout) {
        this.isInitializing = false;
      }
    });
  }

  ngOnInit() {
    // Initialize language with EkoLanguageService
    this.setupLanguageService();
    this.setupUserMenu();
    this.menuItemsInit();

    // Subscribe to user changes and setup profile
    this.store.select(selectUser).pipe(
      takeUntil(this.destroy$)
    ).subscribe(user => {
      this.user = user;
      if (user) {
        this.initNavigationMenu();
        // Map user data to currentUser for UI library
        this.setupUserProfileFromStore(user);
      }
    });

    // Wait for both user and companies to load before hiding splash screen
    combineLatest([
      this.store.select(selectUser),
      this.store.select(selectCompanies)
    ]).pipe(
      filter(([user, companies]) => user !== null && companies !== null),
      take(1)
    ).subscribe(([user, companies]) => {
      this.isInitializing = false;

      if(companies?.length === 0) {
        this.handleNoCompanies();
      } else {
        this.hasCompanies = true;
      }
    });

    this.store.select(selectCompanyContext).pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      if(!state.error && state.companies && state.companies.length === 0) {
        this.handleNoCompanies();
      }
    });

    // Setup token refresh handler
    if (this.keycloak) {
      this.keycloak.onTokenExpired = () => {
        this.keycloak.updateToken(30).then(() => {}).catch(() => {
          this.keycloak.logout();
        });

        this.keycloak.onAuthError = (error: any) => {
          if (error.error === "invalid_token" || error.error === "access_denied") {
            this.keycloak.logout();
          }
        };
      };
    }
  }

  handleNoCompanies() {
    if (this.noCompanyDialog) {
      this.noCompanyDialog.closable = false;
    }

    this.confirmationService.confirm({
      header: this.translate.instant('views.app.no_companies.title'),
      message: this.translate.instant('views.app.no_companies.message'),
      acceptVisible: true,
      closeOnEscape: false,
      acceptLabel: this.translate.instant('views.app.no_companies.go_to_add_company'),
      rejectLabel: this.translate.instant('views.app.no_companies.logout'),
      reject: () => {
        this.keycloak.logout();
      },
      accept: () => {
        //redirect to external website
        window.location.href = 'https://panel.bdomobile.com/dashboard/companies';
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  handleRefreshData() {
    this.menuItemsInit();
  }

  menuItemsInit(){
    // Removed menuItems - using only navigationItems for unified menu system
  }

  private hasConferenceAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_conferences_management') ?? false;
  }

  private hasTransportAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_transport_access') ?? false;
  }

  private hasScope3Access(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_access') ?? false;
  }

  private hasScope3EmployeeCommuteAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_employee_commute_access') ?? false;
  }

  private hasScope3PurchasedGoodsServicesAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_purchased_goods_services_access') ?? false;
  }

  private hasSoldProductUsageAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_sold_product_usage_access') ?? false;
  }

  initNavigationMenu() {
    this.navigationItems = [
        {
          label: this.translate.instant('views.app.menu.dashboard'),
          icon: 'pi pi-home',
          routerLink: '/dashboard',
          routerLinkActiveOptions: { exact: true }
        },
        {
          label: this.translate.instant('views.app.menu.scope1'),
          icon: 'pi pi-chart-bar',
          expanded: true,
          items: [
            {
              label: this.translate.instant('views.app.menu.scope1.vehicle'),
              icon: 'pi pi-car',
              routerLink: '/dashboard/scope1/vehicles',
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope1.fugitiveEmission'),
              icon: 'pi pi-cloud',
              routerLink: '/dashboard/scope1/fugitiveEmission',
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope1.stationaryDevice'),
              icon: 'pi pi-cog',
              routerLink: '/dashboard/scope1/stationaryDevice',
              routerLinkActiveOptions: { exact: true }
            }
          ]
        },
        {
          label: this.translate.instant('views.app.menu.scope2'),
          icon: 'pi pi-chart-line',
          expanded: true,
          items: [
            {
              label: this.translate.instant('views.app.menu.scope2.heatConsumption'),
              icon: 'pi pi-sun',
              routerLink: '/dashboard/scope2/heatConsumption',
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope2.electricityConsumption'),
              icon: 'pi pi-bolt',
              routerLink: '/dashboard/scope2/electricity',
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope2.renewableEnergyConsumption'),
              icon: 'pi pi-refresh',
              routerLink: '/dashboard/scope2/renewableEnergyConsumption',
              routerLinkActiveOptions: { exact: true }
            }
          ]
        },
        {
          label: this.translate.instant('views.app.menu.scope3'),
          icon: 'pi pi-chart-pie',
          expanded: true,
          badge: !this.hasScope3Access() ? 'Wykup dostęp' : '',
          disabled: !this.hasScope3Access(),
          items: [
            {
              label: this.translate.instant('views.app.menu.scope3.purchasedGoodsServices'),
              icon: 'pi pi-shopping-cart',
              routerLink: '/dashboard/scope3/purchased-goods-services',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasScope3PurchasedGoodsServicesAccess(),
              tooltip: !this.hasScope3Access() || this.hasScope3PurchasedGoodsServicesAccess() ? '' : this.translate.instant('views.app.menu.notAvailable'),
              visible: this.hasScope3Access()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.soldProductUsage'),
              icon: 'pi pi-box',
              routerLink: '/dashboard/scope3/sold-product-usage',
              disabled: !this.hasScope3Access() || !this.hasSoldProductUsageAccess(),
              tooltip: !this.hasScope3Access() || this.hasSoldProductUsageAccess() ? '' : this.translate.instant('views.app.menu.notAvailable'),
              visible: this.hasScope3Access(),
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope3.transport_upstream'),
              icon: 'pi pi-truck',
              routerLink: '/dashboard/scope3/transport/upstream',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasTransportAccess(),
              tooltip: !this.hasScope3Access() || this.hasTransportAccess() ? '' : this.translate.instant('views.app.menu.no_transport_access'),
              visible: this.hasScope3Access()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.transport_downstream'),
              icon: 'pi pi-send',
              routerLink: '/dashboard/scope3/transport/downstream',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasTransportAccess(),
              tooltip: !this.hasScope3Access() || this.hasTransportAccess() ? '' : this.translate.instant('views.app.menu.no_transport_access'),
              visible: this.hasScope3Access()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.employee_commute'),
              icon: 'pi pi-users',
              routerLink: '/dashboard/scope3/employee-commute',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasScope3EmployeeCommuteAccess(),
              tooltip: !this.hasScope3Access() || this.hasScope3EmployeeCommuteAccess() ? '' : this.translate.instant('views.app.menu.notAvailable'),
              visible: this.hasScope3Access()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.conferences'),
              icon: 'pi pi-users',
              routerLink: '/dashboard/scope3/conferences',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasConferenceAccess(),
              tooltip: this.hasConferenceAccess() ? '' : this.translate.instant('views.app.menu.no_conference_access'),
              visible: this.hasConferenceAccess()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.remoteWork'),
              icon: 'pi pi-home',
              routerLink: '/dashboard/scope3/remoteWork',
              disabled: true,
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope3.businessTravel'),
              icon: 'pi pi-briefcase',
              routerLink: '/dashboard/scope3/businessTravel',
              disabled: true,
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope3.capitalGoods'),
              icon: 'pi pi-building',
              routerLink: '/dashboard/scope3/capitalGoods',
              disabled: true,
              routerLinkActiveOptions: { exact: true }
            }
          ]
        }
      ];
  }

  setupLanguageService() {
    // Initialize current language from EkoLanguageService
    this.currentLanguage = this.languageService.currentLanguage;

    // Subscribe to language changes from EkoLanguageService
    this.languageService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang: string) => {
        this.currentLanguage = lang;
      });

    // Subscribe to TranslateService language changes to update UI
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.primengTranslationService.setTranslations();
        this.pageTitle = this.translate.instant('app.title');
        this.initNavigationMenu();
        this.setupUserMenu();
      });
  }

  setupUserProfileFromStore(user: User) {
    // Map user data from store to UI library format
    try {
      const firstName = user.token?.given_name || user.profile?.firstName || '';
      const lastName = user.token?.family_name || user.profile?.lastName || '';
      const displayName = firstName && lastName ? `${firstName} ${lastName}` :
                         user.token?.name || user.token?.preferred_username || user.profile?.username || 'Użytkownik';

      this.currentUser = {
        name: displayName,
        email: user.token?.preferred_username || user.token?.email || user.profile?.email || user.profile?.username || '',
        role: user.roles?.join(', ') || ''
      };
    } catch (error) {
      this.currentUser = {
        name: user.profile?.username || 'Użytkownik',
        email: user.profile?.email || user.profile?.username || ''
      };
    }
  }

  setupUserMenu() {
    this.userMenuItems = [
     
    ];
  }

  onLanguageChange(language: string) {
    // Save language preference
    localStorage.setItem('appLang', language);
    // Use TranslateService to change language
    this.translate.use(language);
  }

  updatePageTitle() {
    this.translate.get('app.title').pipe(
      takeUntil(this.destroy$)
    ).subscribe(title => {
      this.pageTitle = title;
    });
  }

  onMenuClick(item: NavigationItem) {
    if (item.routerLink) {
      const route = Array.isArray(item.routerLink) ? item.routerLink.join('/') : item.routerLink;

      this.router.navigateByUrl(route).then((success) => {
        if (success) {
          setTimeout(() => {
            this.scrollToTop();
          }, 200);
        }
      });
    }
  }

  onMenuClosed() {
    // Handler for mobile menu close
  }

  onProfileAction(action: string) {
    switch (action) {
      case 'profile':
        // Navigate to profile page
        break;
      case 'logout':
        this.logout();
        break;
    }
  }

  onProfileMenuClick(menuItem: MenuItem) {
    if (menuItem.command) {
      menuItem.command({});
    }
  }

  scrollToTop() {
    // Scroll window
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    
    // Scroll main content container
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      mainContent.scrollTop = 0;
    }
    
    // Scroll router-outlet container
    const routerOutlet = document.querySelector('router-outlet');
    if (routerOutlet && routerOutlet.parentElement) {
      routerOutlet.parentElement.scrollTo({ top: 0, behavior: 'smooth' });
      routerOutlet.parentElement.scrollTop = 0;
    }
    
    // Scroll layout sidebar content area
    const sidebarContent = document.querySelector('eko-layout-sidebar .flex-1.bg-gray-50, eko-layout-sidebar .flex-1.bg-gray-900');
    if (sidebarContent) {
      (sidebarContent as any).scrollTo({ top: 0, behavior: 'smooth' });
      (sidebarContent as any).scrollTop = 0;
    }
    
    // Scroll all potential scrollable elements
    const scrollableElements = document.querySelectorAll('[class*="overflow"], .flex-1, .min-h-screen, [class*="scroll"], body, html');
    scrollableElements.forEach((el: any) => {
      try {
        if (el.scrollTo) el.scrollTo({ top: 0, behavior: 'smooth' });
        el.scrollTop = 0;
      } catch (e) {
        // Ignore scroll errors
      }
    });
  }

  logout() {
    this.keycloak.logout();
  }

  onSidebarToggle() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  get currentRoute(): string {
    return this.router.url;
  }

}
